import fallback from '@/assets/images/cross-card-cover.png';
import { IconFont, UploadEntrance, isImage } from '@/components';
import { needTemplateCategoryParam, QUERY_KEYS } from '@/constants';
import { createTask } from '@/services/templateCenter';
import type { ITemplate } from '@/services/workbench';
import { deleteFavoriteTemplate } from '@/services/workbench';
import { Button } from '@ai-indeed/idp-ui';
import { Link, useMutation, useNavigate, useQueryClient } from '@umijs/max';
import { Dropdown, Tooltip, message } from 'antd';
import type { FC } from 'react';
import './index.less';

export const TemplateCard: FC<{
  dataSource: ITemplate;
  onBeforeUpload: (value: ITemplate) => void;
  onPreview: (templateCode: string) => void;
  onUploaded: (fileList: any[], template: ITemplate) => void;
}> = (props) => {
  // props
  const { dataSource, onPreview, onUploaded } = props;
  const {
    cover_image,
    template_code,
    template_name,
    module,
    is_builtin,
    file_extension_constraints,
    template_category,
  } = dataSource;

  // navigate
  const navigate = useNavigate();
  // query client
  const queryClient = useQueryClient();
  // mutation
  const { mutate: deleteTemplate } = useMutation(deleteFavoriteTemplate, {
    onSuccess(res) {
      const { code, msg } = res;
      if (code !== 0) {
        message.error(msg || '取消失败');
        return;
      }
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.favoriteTemplateList],
      });
      message.success('取消成功');
    },
    onError() {
      message.error('服务器错误');
    },
  });
  const { mutateAsync: create } = useMutation(createTask);

  const handleClickUploadBtn = () => {
    props.onBeforeUpload(dataSource);
  };

  return (
    <section className="tc-wrapper relative z-10 inline-flex flex-col rounded-md overflow-hidden transition">
      <div
        className="tc-poster group"
        style={{
          backgroundImage: `url("${
            cover_image || fallback
          }"), linear-gradient(180deg, #F2F7FD 0%, #E7F1FE 100%)`,
          backgroundSize: cover_image
            ? module === 'DIFF' ||
              module === 'OCR' ||
              module === 'API' ||
              module === 'TABLE'
              ? '50%, contain'
              : 'contain'
            : '56px 50px, contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="tc-mask relative hidden justify-center items-center h-full group-hover:flex">
          <Tooltip title="历史记录" placement="bottom">
            <Link
              className="tc-float__button absolute top-3 left-3 flex justify-center items-center bg-white rounded"
              to={`/task-list?id=${template_code}&name=${template_name}`}
            >
              <IconFont
                className="text-sm hover:text-primary"
                type="icon-zuijinrenwu"
              />
            </Link>
          </Tooltip>
          <div className="absolute top-3 right-3 flex items-center bg-white rounded-[5px]">
            {is_builtin && (
              <>
                <Tooltip title="预览" placement="bottom">
                  <button
                    type="button"
                    className="tc-float__button flex justify-center items-center"
                    onClick={() => onPreview(template_code)}
                  >
                    <IconFont
                      className="text-sm hover:text-primary"
                      type="icon-yulan"
                    />
                  </button>
                </Tooltip>
                <div className="w-px h-[14px] bg-[#E4E8EE]"></div>
              </>
            )}
            <Dropdown
              arrow={{ pointAtCenter: true }}
              menu={{
                items: [
                  {
                    key: 'button',
                    label: '取消收藏',
                  },
                ],
                onClick: () => deleteTemplate(template_code),
              }}
            >
              <button
                type="button"
                className="flex justify-center items-center w-[26px] h-[26px]"
              >
                <IconFont
                  className="text-sm hover:text-primary"
                  type="icon-gengduo1"
                />
              </button>
            </Dropdown>
          </div>
          {['SCENE', 'DIFF'].includes(module) ? (
            <Button
              className="mt-[30px]"
              type="primary"
              size="large"
              style={{ width: 118 }}
              onClick={handleClickUploadBtn}
            >
              {module === 'SCENE' ? '新建任务' : '上传文件'}
            </Button>
          ) : (
            <UploadEntrance
              module={module}
              accept={file_extension_constraints.join(',')}
              data={{
                module,
              }}
              finished={(fileList) => {
                if (
                  fileList.some(
                    (file) =>
                      file.error ||
                      file.status === 'error' ||
                      file.response.code !== 0,
                  )
                ) {
                  message.error('文件上传失败');
                  return;
                }
                if (
                  fileList.every(
                    (file) =>
                      file.status === 'done' && file.response.code === 0,
                  ) &&
                  (fileList.length === 1 ||
                    fileList.every((file) => file.type && isImage(file.type)))
                ) {
                  const { data } = fileList[0].response as IRes<{
                    fileHash: string;
                    filename: string;
                  }>;
                  create({
                    template_codes: [template_code],
                    tasks: [
                      {
                        task_name: data.filename,
                        file_hash_list: fileList.map((file) => {
                          const { data } = file.response as IRes<{
                            fileHash: string;
                            filename: string;
                          }>;
                          return data.fileHash;
                        }),
                      },
                    ],
                    ...(needTemplateCategoryParam.includes(module) && { template_categories: [template_category] }),
                  }).then((res) => {
                    const { code, data } = res;

                    if (code !== 0) {
                      return;
                    }
                    const taskCode = data[0];
                    let pathname = '/text-detail';
                    let search = `task-code=${taskCode}`;

                    switch (module) {
                      case 'TABLE': {
                        pathname = '/recognition-detail';
                        break;
                      }
                      default: {
                        if (data.length > 1) {
                          search += `&task-codes=${data.join(',')}`;
                        }
                      }
                    }

                    message.success('创建成功').then(() => {
                      navigate({
                        pathname,
                        search,
                      });
                    });
                  });
                  return;
                }
                onUploaded(fileList, dataSource);
              }}
            >
              <Button
                className="mt-[30px]"
                type="primary"
                size="large"
                style={{ width: 118 }}
                onClick={handleClickUploadBtn}
              >
                上传文件
              </Button>
            </UploadEntrance>
          )}
        </div>
      </div>
      <div className="flex items-center flex-1 px-4 cursor-pointer group">
        <Tooltip title={template_name}>
          <p className="truncate text-sm text-main transition group-hover:text-primary">
            {template_name}
          </p>
        </Tooltip>
      </div>
    </section>
  );
};
